import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Star } from 'lucide-react';

/**
 * Displays the Key Associations card in the Account Overview.
 *
 * @param {object}   account           — the CRM account
 * @param {array}    keyAssociations   — filtered key associations (pre-derived in parent)
 * @param {object}   entityMap         — id → entity lookup (pre-derived in parent)
 * @param {function} onAssociationClick — called with (assoc) when a row is clicked
 */
const AccountAssociationsCard = ({ account, keyAssociations, entityMap, onAssociationClick }) => {
  const navigate = useNavigate();

  const getName = (entityId) => entityMap[entityId]?.system_name || 'Unknown Entity';
  const otherId = (assoc) => assoc.crm_entity_1_id === account?.id ? assoc.crm_entity_2_id : assoc.crm_entity_1_id;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Key Associations</CardTitle>
      </CardHeader>
      <CardContent>
        {keyAssociations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No key associations marked.</p>
        ) : (
          <div className="space-y-2">
            {keyAssociations.map(assoc => {
              const id = otherId(assoc);
              return (
                <div
                  key={assoc.id}
                  className="flex items-center justify-between p-2 border rounded hover:bg-muted/50 cursor-pointer group"
                  onClick={() => onAssociationClick?.(assoc)}
                  data-testid={`association-${assoc.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    <span
                      className="font-medium text-sm text-primary hover:underline cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); navigate(`/crm/accounts/${id}`); }}
                    >
                      {getName(id)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs font-normal">{assoc.relationship}</Badge>
                    <Badge variant={assoc.status?.includes('confirmed') ? 'default' : 'outline'} className="text-[10px] h-5">
                      {assoc.status?.includes('confirmed') ? 'Confirmed' : 'Possible'}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccountAssociationsCard;
